// pages/stock/StockForm.jsx
import React, { useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Alert,
    InputAdornment,
} from '@mui/material';
import {
    Warning as WarningIcon,
    Inventory as InventoryIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { stockAPI } from '../../services/api';

// ─── Validation schema ────────────────────────────────────────────────────
const buildStockSchema = (t) =>
    Yup.object().shape({
        name: Yup.string().required(t('stock.validation.nameRequired', 'Stock name is required')),
        unit: Yup.string().required(t('stock.validation.unitRequired', 'Unit is required')),
        category: Yup.string().nullable(),
        quantities: Yup.object()
            .test(
                'at-least-one',
                t('stock.validation.quantityRequired', 'At least one branch quantity is required'),
                (value) =>
                    value &&
                    Object.values(value).some(
                        (v) => v !== undefined && v !== null && v !== ''
                    )
            )
            .required(t('stock.validation.quantityRequired', 'Quantities are required')),
        minimumThreshold: Yup.number()
            .min(0, t('stock.validation.thresholdMin', 'Minimum threshold cannot be negative'))
            .required(t('stock.validation.thresholdRequired', 'Minimum threshold is required')),
        price: Yup.number()
            .min(0, t('stock.validation.priceMin', 'Price cannot be negative'))
            .required(t('stock.validation.priceRequired', 'Price is required')),
        validUntil: Yup.date().required(
            t('stock.validation.validUntilRequired', 'Valid until date is required')
        ),
    });

const StockForm = ({
    open,
    onClose,
    selectedItem,
    branches,
    categories,
    categoriesLoading,
    onSuccess,
    isSuperAdmin,
    user,
}) => {
    const { t } = useTranslation();

    // ── Helper: Get branch WAC for display ──────────────────────────────
    const getBranchWac = useCallback((item, branchId) => {
        if (!item) return 0;
        if (item.branchWac) {
            if (typeof item.branchWac === 'object') {
                return item.branchWac[branchId] || 0;
            }
        }
        if (item.currentWac !== undefined) {
            return item.currentWac;
        }
        return 0;
    }, []);

    const formik = useFormik({
        initialValues: {
            name: '',
            unit: '',
            category: '',
            quantities: {},
            minimumThreshold: '',
            price: '',
            validUntil: null,
        },
        validationSchema: buildStockSchema(t),
        enableReinitialize: false,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                const fixedQuantities = { ...values.quantities };
                branches.forEach((branch) => {
                    fixedQuantities[branch._id] = Number(fixedQuantities[branch._id] ?? 0);
                });

                const payload = {
                    ...values,
                    quantities: fixedQuantities,
                    category: values.category === '' ? null : values.category,
                };

                if (selectedItem) {
                    await stockAPI.update(selectedItem._id, payload);
                    toast.success(t('stock.updateSuccess', 'Stock item updated successfully'));
                } else {
                    await stockAPI.create(payload);
                    toast.success(t('stock.createSuccess', 'Stock item created successfully'));
                }

                onSuccess();
                handleClose();
            } catch (err) {
                toast.error(
                    err.response?.data?.message || t('stock.error', 'An error occurred')
                );
            } finally {
                setSubmitting(false);
            }
        },
    });

    // ── Populate form when selectedItem changes or dialog opens ──────────
    useEffect(() => {
        if (!open) return;

        if (selectedItem) {
            formik.setValues({
                name: selectedItem.name ?? '',
                unit: selectedItem.unit ?? '',
                category: selectedItem.category?._id ?? selectedItem.category ?? '',
                price: selectedItem.price ?? '',
                minimumThreshold: selectedItem.minimumThreshold ?? '',
                validUntil: selectedItem.validUntil
                    ? new Date(selectedItem.validUntil)
                    : null,
                quantities: selectedItem.quantities
                    ? { ...selectedItem.quantities }
                    : {},
            });
        } else {
            formik.resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedItem]);

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    // ── Compute quantity change indicators ──────────────────────────────
    const getQuantityDiff = (branchId) => {
        if (!selectedItem || !selectedItem.quantities) return null;
        const original = Number(selectedItem.quantities[branchId] ?? 0);
        const current = Number(formik.values.quantities?.[branchId] ?? 0);
        const diff = current - original;
        if (diff === 0) return null;
        return diff;
    };

    const isEditing = !!selectedItem;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InventoryIcon color="primary" />
                {isEditing
                    ? t('stock.editItem', 'Edit Stock Item')
                    : t('stock.addItem', 'Add Stock Item')}
            </DialogTitle>

            <DialogContent>
                <Box
                    component="form"
                    id="stockForm"
                    onSubmit={formik.handleSubmit}
                    sx={{ pt: 1 }}
                >
                    <Grid container spacing={2}>
                        {/* ── Name ── */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('stock.name', 'Name')}
                                name="name"
                                {...formik.getFieldProps('name')}
                                error={formik.touched.name && !!formik.errors.name}
                                helperText={formik.touched.name && formik.errors.name}
                                autoFocus
                            />
                        </Grid>

                        {/* ── Unit ── */}
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                label={t('stock.unit', 'Unit')}
                                name="unit"
                                {...formik.getFieldProps('unit')}
                                error={formik.touched.unit && !!formik.errors.unit}
                                helperText={formik.touched.unit && formik.errors.unit}
                            />
                        </Grid>

                        {/* ── Category ── */}
                        <Grid item xs={12} sm={3}>
                            <FormControl
                                fullWidth
                                error={formik.touched.category && !!formik.errors.category}
                            >
                                <InputLabel>
                                    {categoriesLoading
                                        ? t('common.loading', 'Loading...')
                                        : t('stock.category', 'Category')}
                                </InputLabel>
                                <Select
                                    name="category"
                                    value={formik.values.category}
                                    onChange={formik.handleChange}
                                    label={t('stock.category', 'Category')}
                                    disabled={categoriesLoading}
                                >
                                    <MenuItem value="">
                                        <em>{t('common.none', 'None')}</em>
                                    </MenuItem>
                                    {categories.map((cat) => (
                                        <MenuItem key={cat._id} value={cat._id}>
                                            {cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formik.touched.category && formik.errors.category && (
                                    <FormHelperText>{formik.errors.category}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>

                        {/* ── Quantities per Branch ── */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {t('stock.quantitiesPerBranch', 'Quantities per Branch')}
                            </Typography>

                            {isEditing && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    {t(
                                        'stock.quantityEditWarning',
                                        'Editing quantities directly will adjust the stock count. For regular stock movements, use orders or transfers instead.'
                                    )}
                                </Alert>
                            )}

                            {branches.map((branch) => {
                                const fieldName = `quantities.${branch._id}`;
                                const currentValue = formik.values.quantities?.[branch._id] ?? '';
                                const diff = getQuantityDiff(branch._id);
                                const wac = isEditing ? getBranchWac(selectedItem, branch._id) : 0;
                                const isLow =
                                    Number(currentValue || 0) <=
                                    Number(formik.values.minimumThreshold || 0);

                                return (
                                    <Box
                                        key={branch._id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            my: 1,
                                            py: 1,
                                            px: 1.5,
                                            borderRadius: 1,
                                            backgroundColor: isLow
                                                ? 'rgba(255, 152, 0, 0.04)'
                                                : 'transparent',
                                            border: '1px solid',
                                            borderColor: isLow ? 'warning.light' : 'divider',
                                        }}
                                    >
                                        {/* Branch Name */}
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight="medium">
                                                {branch.name}
                                            </Typography>
                                            {isLow && currentValue !== '' && (
                                                <WarningIcon fontSize="small" color="warning" />
                                            )}
                                        </Box>

                                        {/* Quantity Input */}
                                        <TextField
                                            type="number"
                                            name={fieldName}
                                            value={currentValue}
                                            onChange={formik.handleChange}
                                            sx={{ width: 140 }}
                                            size="small"
                                            inputProps={{ min: 0 }}
                                            InputProps={{
                                                endAdornment: formik.values.unit ? (
                                                    <InputAdornment position="end">
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formik.values.unit}
                                                        </Typography>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />

                                        {/* Diff indicator (edit mode) */}
                                        {isEditing && diff !== null && (
                                            <Chip
                                                label={`${diff > 0 ? '+' : ''}${diff}`}
                                                size="small"
                                                color={diff > 0 ? 'success' : 'error'}
                                                variant="outlined"
                                                sx={{ ml: 1, minWidth: 60 }}
                                            />
                                        )}

                                        {/* WAC display (edit mode) */}
                                        {isEditing && wac > 0 && (
                                            <Chip
                                                label={`WAC: ${wac.toFixed(2)}`}
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </Box>
                                );
                            })}

                            {/* Quantities validation error */}
                            {formik.touched.quantities && formik.errors.quantities && (
                                <FormHelperText error sx={{ mt: 1 }}>
                                    {typeof formik.errors.quantities === 'string'
                                        ? formik.errors.quantities
                                        : t(
                                            'stock.validation.quantityRequired',
                                            'At least one branch quantity is required'
                                        )}
                                </FormHelperText>
                            )}
                        </Grid>

                        {/* ── Price ── */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t('stock.price', 'Price')}
                                name="price"
                                type="number"
                                {...formik.getFieldProps('price')}
                                error={formik.touched.price && !!formik.errors.price}
                                helperText={formik.touched.price && formik.errors.price}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Grid>

                        {/* ── Minimum Threshold ── */}
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label={t('stock.minimumThreshold', 'Min. Threshold')}
                                name="minimumThreshold"
                                type="number"
                                {...formik.getFieldProps('minimumThreshold')}
                                error={
                                    formik.touched.minimumThreshold &&
                                    !!formik.errors.minimumThreshold
                                }
                                helperText={
                                    formik.touched.minimumThreshold &&
                                    formik.errors.minimumThreshold
                                }
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        {/* ── Valid Until ── */}
                        <Grid item xs={12} sm={4}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label={t('stock.validUntil', 'Valid Until')}
                                    value={formik.values.validUntil}
                                    onChange={(date) => formik.setFieldValue('validUntil', date)}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error:
                                                formik.touched.validUntil && !!formik.errors.validUntil,
                                            helperText:
                                                formik.touched.validUntil && formik.errors.validUntil,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={formik.isSubmitting}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                    type="submit"
                    form="stockForm"
                    variant="contained"
                    disabled={formik.isSubmitting || !formik.isValid}
                >
                    {formik.isSubmitting ? (
                        <CircularProgress size={24} />
                    ) : isEditing ? (
                        t('common.update', 'Update')
                    ) : (
                        t('common.create', 'Create')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockForm;